import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { RiRefreshLine } from "@remixicon/react";
import { countContactNotes, getContactNotesPage, getStudentContactNotes, NOTES_PAGE_SIZE } from "../../services/staffNotes";
import {
  CONTACT_FILTERS,
  RANGE_OPTIONS,
  contactEmptyText,
  filterContactNotes,
  interestChip,
  localContactCounts,
  mergeContactNotes,
  rangeFor,
} from "../../utils/staffNotesPresentation";
import { dateTimeLines, showingLabel } from "../../utils/kitPresentation";
import { isSearchTerm } from "../../utils/studentPresentation";
import { personName } from "../../utils/studentDetail";
import { PHONE_ALIASES, searchPeople } from "../../services/dataService";
import { headButtonSx, tokens } from "../../utils/figmaTokens";
import { useBreadcrumbName } from "../../components/Layout/BreadcrumbContext";
import { ListHead } from "../../components/kit/PageHead";
import { TableCard } from "../../components/kit/SectionCard";
import SearchField from "../../components/kit/SearchField";
import SegmentTabs from "../../components/kit/SegmentTabs";
import StatusChip from "../../components/kit/StatusChip";
import Pager from "../../components/kit/Pager";
import { PersonCell, SkeletonRows, TableMessageRow, TwoLineCell } from "../../components/kit/TableParts";
import { errorText } from "./tabs/TabState";
import { usePagedQuery } from "./tabs/usePagedQuery";
import { useUsersByIds } from "./tabs/useUsersByIds";

const COLUMNS = ["Student", "Date", "By", "Reply", "Interested", "What they said"];
const SEARCH_STUDENTS_MAX = 10;
const NO_COUNTS = Object.fromEntries(CONTACT_FILTERS.map((f) => [f.key, null]));
const EMPTY_PAGE = { rows: [], cursor: null, hasMore: false };

const phoneOf = (u) => PHONE_ALIASES.map((f) => u?.[f]).find(Boolean) || "";

// The Contact report (spec 059 D11, D12, D18–D20): every contact note staff wrote on any student,
// newest first, under one of CONTACT_FILTERS with a count per filter, within a date range. A
// search for a student swaps the paged collection-group read for each matched student's own
// notes, merged and filtered in memory, with the counts taken from that list. Refresh moves
// `now` on, which re-reads the rows (from the first page), the counts and an open search. A row
// opens the student's Notes tab. Reads only through services/staffNotes.js and searchPeople.
const ContactReport = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [rangeKey, setRangeKey] = useState("all");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [now, setNow] = useState(() => new Date());
  const [term, setTerm] = useState("");
  const [search, setSearch] = useState(null); // null while no search is on
  const [counts, setCounts] = useState(NO_COUNTS);
  useBreadcrumbName("contact-report", "Contact report");

  const range = useMemo(() => rangeFor(rangeKey, custom, now), [rangeKey, custom, now]);
  const rangeOk = !range.error;

  const fetchPage = useCallback(
    (cursor) => (rangeOk ? getContactNotesPage(filter, { cursor, pageSize: NOTES_PAGE_SIZE, range }) : Promise.resolve(EMPTY_PAGE)),
    [filter, range, rangeOk]
  );
  const notes = usePagedQuery(fetchPage, filter, "Failed to load the contact notes.");

  // One count per filter for the range; again when the range moves. A count that fails stays "—".
  useEffect(() => {
    setCounts(NO_COUNTS);
    if (!rangeOk) return undefined;
    let cancelled = false;
    CONTACT_FILTERS.forEach((f) => {
      countContactNotes(f.key, range)
        .then((n) => !cancelled && setCounts((prev) => ({ ...prev, [f.key]: n })))
        .catch((e) => console.error(`Error counting ${f.label} contact notes:`, e));
    });
    return () => {
      cancelled = true;
    };
  }, [range, rangeOk]);

  // Search (D18): the Students page's debounce and minimum, then each matched student's notes
  // in the range, merged newest first. A newer term or range drops an older answer.
  useEffect(() => {
    if (!isSearchTerm(term) || !rangeOk) {
      setSearch(null);
      return undefined;
    }
    let stale = false;
    setSearch({ loading: true, rows: [], matched: 0 });
    const timer = setTimeout(async () => {
      try {
        const people = await searchPeople(term, { role: "student" });
        const lists = await Promise.all(people.slice(0, SEARCH_STUDENTS_MAX).map((p) => getStudentContactNotes(p.id, range)));
        if (!stale) setSearch({ loading: false, rows: mergeContactNotes(lists), matched: people.length });
      } catch (e) {
        console.error("Error searching contact notes:", e);
        if (!stale) setSearch({ loading: false, rows: [], matched: 0, error: errorText(e, "Failed to search the contact notes.") });
      }
    }, 300);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [term, range, rangeOk]);

  const rows = search ? filterContactNotes(search.rows, filter) : notes.rows;
  const loading = search ? search.loading : notes.loading;
  const error = search ? search.error : notes.error;
  const users = useUsersByIds(rows.map((n) => n.studentId));

  const settled = !loading && !error;
  const overflow = search && !search.loading && search.matched > SEARCH_STUDENTS_MAX;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
        <ListHead caption="People" title="Contact report" subtitle="Every contact note staff wrote on a student, newest first" />
        <Button onClick={() => setNow(new Date())} disableRipple disableFocusRipple sx={headButtonSx}>
          <RiRefreshLine size={18} />
          Refresh
        </Button>
      </Box>

      <TableCard
        toolbar={
          <>
            <SearchField value={term} onChange={setTerm} placeholder="Search a student by name or phone…" />
            <SegmentTabs label="Contact outcome" tabs={CONTACT_FILTERS} value={filter} counts={!search ? counts : search.loading ? NO_COUNTS : localContactCounts(search.rows)} onChange={setFilter} />
            <SegmentTabs label="Date range" tabs={RANGE_OPTIONS} value={rangeKey} onChange={setRangeKey} />
            {rangeKey === "custom" && (
              <>
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  value={custom.from}
                  onChange={(e) => setCustom({ ...custom, from: e.target.value })}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: custom.to || undefined } }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  value={custom.to}
                  onChange={(e) => setCustom({ ...custom, to: e.target.value })}
                  error={!rangeOk}
                  helperText={range.error}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: custom.from || undefined } }}
                />
              </>
            )}
            {overflow && (
              <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary, flexBasis: "100%" }}>
                {search.matched} students match. Showing notes for the first {SEARCH_STUDENTS_MAX}; narrow the search for the rest.
              </Typography>
            )}
          </>
        }
        footer={!search && <Pager label={settled ? showingLabel(notes.page, notes.rows.length, NOTES_PAGE_SIZE) : ""} page={notes.page} hasMore={notes.hasMore} onPage={notes.load} />}
      >
        <TableContainer>
          <Table aria-busy={loading}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((c) => (
                  <TableCell key={c}>{c}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <SkeletonRows columns={COLUMNS.length} rows={5} />
              ) : error || rows.length === 0 ? (
                <TableMessageRow
                  colSpan={COLUMNS.length}
                  error={error}
                  onRetry={() => (search ? setNow(new Date()) : notes.load(notes.page))}
                  emptyText={contactEmptyText({ rangeKey, term: search ? term.trim() : "" })}
                  emptyMessage={search ? (search.matched ? "Try another outcome or a wider date range." : "No student matches that name or phone.") : "A Contact note on a student's page lands here."}
                />
              ) : (
                rows.map((n) => {
                  const student = users[n.studentId];
                  const when = dateTimeLines(n.createdAt);
                  const interest = n.replied ? interestChip(n.interested) : null;
                  return (
                    <TableRow key={n.id} hover onClick={() => navigate(`/students/${n.studentId}/notes`)} sx={{ cursor: "pointer" }}>
                      <TableCell>
                        <PersonCell name={student ? personName(student) || "Unnamed student" : "…"} colorKey={n.studentId} sub={student ? phoneOf(student) || student.email || "" : ""} />
                      </TableCell>
                      <TableCell>
                        <TwoLineCell top={when ? when.date : "—"} bottom={when?.time} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{n.authorName || "—"}</TableCell>
                      <TableCell>
                        <StatusChip label={n.replied ? "Replied" : "No reply"} color={n.replied ? "info" : "default"} />
                      </TableCell>
                      <TableCell>
                        {interest ? (
                          <StatusChip label={interest.label} color={interest.color} />
                        ) : (
                          <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 220, maxWidth: 420 }}>
                        {n.text ? (
                          <Typography
                            variant="bodySmall"
                            title={n.text}
                            sx={{
                              color: tokens.text.primary,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              whiteSpace: "pre-wrap",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {n.text}
                          </Typography>
                        ) : (
                          <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>
    </Box>
  );
};

export default ContactReport;
